// Manual ad-upload dialog on the Fetched Ads page. Replaces the standalone
// Transcription tab: the user enters the same metadata fields a scraped ad
// has, supplies a video (URL or file), and submit creates a CI_ADS row +
// auto-runs the standard transcribe → analyze → extract pipeline.

import React, { useState, useRef, useEffect } from 'react';
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import UploadFileIcon from '@mui/icons-material/UploadFile';

import AdLibraryService from 'services/AdLibraryService';
import { Keyword } from './types';

const service = new AdLibraryService();

interface UploadAdDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (opts: { adId: string; currentStep?: string }) => void;
}

export default function UploadAdDialog({ open, onClose, onSuccess }: UploadAdDialogProps) {
  const [pageName, setPageName] = useState('');
  const [headline, setHeadline] = useState('');
  const [caption, setCaption] = useState('');
  const [description, setDescription] = useState('');
  const [cta, setCta] = useState('');
  const [landingPageUrl, setLandingPageUrl] = useState('');
  const [platforms, setPlatforms] = useState('');
  const [startDate, setStartDate] = useState('');
  const [sourcePlatform, setSourcePlatform] = useState('manual');

  // Optional keyword tag — same SCRAPE_KEYWORDS entries the scraper uses,
  // so the Fetched Ads keyword filter and per-keyword aggregations see
  // manually-uploaded ads alongside scraped ones. Lazy-loaded on open.
  const [keywords, setKeywords] = useState<Keyword[]>([]);
  const [keyword, setKeyword] = useState<Keyword | null>(null);

  const [videoMode, setVideoMode] = useState<'url' | 'file'>('url');
  const [videoUrl, setVideoUrl] = useState('');
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setPageName(''); setHeadline(''); setCaption(''); setDescription('');
    setCta(''); setLandingPageUrl(''); setPlatforms(''); setStartDate('');
    setSourcePlatform('manual');
    setKeyword(null);
    setVideoMode('url'); setVideoUrl(''); setVideoFile(null);
    setError(null);
  };

  useEffect(() => {
    if (!open || keywords.length > 0) return;
    service.listKeywords().then(data => { if (data) setKeywords(data.keywords); });
  }, [open, keywords.length]);

  const handleClose = () => {
    if (submitting) return;
    reset();
    onClose();
  };

  // Required: page name, headline, landing page URL, and a video (URL or
  // file). Other metadata fields are optional.
  const hasVideo = videoMode === 'url' ? !!videoUrl.trim() : !!videoFile;
  const canSubmit = !!pageName.trim() && !!headline.trim()
    && !!landingPageUrl.trim() && hasVideo && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);

    const fd = new FormData();
    if (pageName) fd.append('pageName', pageName);
    if (headline) fd.append('headline', headline);
    if (caption) fd.append('caption', caption);
    if (description) fd.append('description', description);
    if (cta) fd.append('cta', cta);
    if (landingPageUrl) fd.append('landingPageUrl', landingPageUrl);
    if (platforms) fd.append('platforms', platforms);
    if (startDate) fd.append('startDate', startDate);
    if (sourcePlatform) fd.append('sourcePlatform', sourcePlatform);
    if (keyword) {
      fd.append('keywordId', keyword.keywordId);
      if (keyword.keyword) fd.append('keyword', keyword.keyword);
    }
    if (videoMode === 'url') fd.append('videoUrl', videoUrl);
    else if (videoFile) fd.append('file', videoFile);

    const result = await service.uploadAd(fd);
    setSubmitting(false);

    if (result?.adId) {
      onSuccess({ adId: result.adId, currentStep: result.currentStep });
      reset();
    } else {
      setError('Upload failed. Check the server logs and try again.');
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Upload ad</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'grid', gap: 1.5 }}>
          <TextField label="Page name" value={pageName} onChange={e => setPageName(e.target.value)}
            size="small" fullWidth required />
          <TextField label="Headline" value={headline} onChange={e => setHeadline(e.target.value)}
            size="small" fullWidth required />
          <TextField label="Landing page URL" value={landingPageUrl}
            onChange={e => setLandingPageUrl(e.target.value)} size="small" fullWidth required />
          <TextField label="Caption (optional)" value={caption} onChange={e => setCaption(e.target.value)}
            size="small" fullWidth multiline minRows={2} />
          <TextField label="Description (optional)" value={description} onChange={e => setDescription(e.target.value)}
            size="small" fullWidth multiline minRows={2} />
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
            <TextField label="CTA (optional)" value={cta} onChange={e => setCta(e.target.value)} size="small" fullWidth />
            <TextField label="Platforms (optional)" placeholder="e.g. facebook,instagram"
              value={platforms} onChange={e => setPlatforms(e.target.value)} size="small" fullWidth />
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
            <TextField label="Start date (optional)" type="date" value={startDate}
              onChange={e => setStartDate(e.target.value)} size="small" fullWidth
              InputLabelProps={{ shrink: true }} />
            <TextField label="Source platform" value={sourcePlatform}
              size="small" fullWidth disabled />
          </Box>
          <Autocomplete<Keyword>
            size="small"
            options={keywords}
            value={keyword}
            onChange={(_, next) => setKeyword(next)}
            getOptionLabel={kw => kw.keyword || `Keyword #${kw.keywordId.slice(0, 8)}`}
            isOptionEqualToValue={(a, b) => a.keywordId === b.keywordId}
            renderInput={params => (
              <TextField {...params} label="Keyword (optional)" placeholder="Tag with an existing scrape keyword" />
            )}
          />

          <Box sx={{ mt: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              Video *
            </Typography>
            <RadioGroup row value={videoMode}
              onChange={e => setVideoMode(e.target.value as 'url' | 'file')}>
              <FormControlLabel value="url" control={<Radio size="small" />} label="URL" />
              <FormControlLabel value="file" control={<Radio size="small" />} label="Upload file" />
            </RadioGroup>
            {videoMode === 'url' ? (
              <TextField placeholder="https://…" value={videoUrl}
                onChange={e => setVideoUrl(e.target.value)} size="small" fullWidth />
            ) : (
              <Box>
                <input ref={fileInputRef} type="file" accept="video/*,audio/*"
                  style={{ display: 'none' }}
                  onChange={e => setVideoFile(e.target.files?.[0] || null)} />
                <Button variant="outlined" size="small" startIcon={<UploadFileIcon />}
                  onClick={() => fileInputRef.current?.click()} disabled={submitting}>
                  {videoFile ? 'Change file' : 'Choose file'}
                </Button>
                {videoFile && (
                  <Chip sx={{ ml: 1 }} size="small"
                    label={`${videoFile.name} (${(videoFile.size / 1024 / 1024).toFixed(1)} MB)`}
                    onDelete={() => setVideoFile(null)}
                    deleteIcon={<CloseIcon />} />
                )}
              </Box>
            )}
          </Box>
        </Box>

        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={!canSubmit}>
          {submitting
            ? <><CircularProgress size={14} sx={{ mr: 1 }} /> Uploading…</>
            : 'Upload & run pipeline'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
